import { Direction } from '@angular/cdk/bidi';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';

import { BehaviorSubject, merge, Observable, Subject } from 'rxjs';
import { map, shareReplay, switchMapTo, tap } from 'rxjs/operators';

import { GenericApiErrorComponent } from '@shared/components/error/generic-api-error/generic-api-error.component';
import { SiteSettingsService } from '@shared/services/site-settings.service';
import { environment } from '../../../environment';
import { ApiEndpoint } from '../enums/api-endpoints.enum';
import { TokeniserSite } from '../enums/tokeniser-site.enum';
import { BinRange, IframeFormValues, IPaymentCardType } from '../interfaces/payment-card-types.interface';
import { LanguageService } from './language-service';

interface TokeniserCredentials {
  apikey: string;
  signature: string;
  timestamp: number;
}

export class TokeniserURL {
  private url: URL;
  private credentialsTimestamp: number;
  private refreshCredentials$: BehaviorSubject<void>;
  private paramsUpdated$: Subject<void>;
  public stringValue$: Observable<string>;

  constructor(
    private snackBar: MatSnackBar,
    credentials$: Observable<TokeniserCredentials>,
    site: TokeniserSite,
    cardType: IPaymentCardType = null,
    fieldValues: IframeFormValues = null,
    language = '',
    direction: Direction = 'ltr',
    cssClass = '',
    clientName = ''
  ) {
    this.refreshCredentials$ = new BehaviorSubject<void>(null);
    this.paramsUpdated$ = new Subject<void>();
    this.url = new URL(environment.tokenizerUrl);
    this.url.searchParams.append('lang', language);
    this.url.searchParams.append('direction', direction);
    this.url.searchParams.append('css-class', cssClass);
    this.url.searchParams.append('client-name', clientName);
    this.url.searchParams.append('task', 'edit');
    this.url.searchParams.append('site', site);
    this.url.searchParams.append('origin', window.origin);
    this.url.searchParams.append('routeId', environment.tokenizerRouteId);
    this.url.searchParams.append('use-public-api', 'true');
    this.setFieldValues(fieldValues);
    this.updateCardTypeInternal(cardType);

    const credentialsUpdated$ = this.refreshCredentials$.pipe(
      switchMapTo(credentials$),
      tap((credentials) => this.setCredentials(credentials))
    );

    this.stringValue$ = merge(credentialsUpdated$, this.paramsUpdated$).pipe(
      map(() => this.url.toString()),
      shareReplay({ bufferSize: 1, refCount: false })
    );
  }

  public updateCardType(cardType: IPaymentCardType, fieldValues?: IframeFormValues) {
    this.setFieldValues(fieldValues);
    this.updateCardTypeInternal(cardType);
    this.paramsUpdated$.next();
  }

  public handleFetchError(error: string, fieldValues?: IframeFormValues) {
    const currentTimestamp = new Date().valueOf();
    const errorThreshold = 1000 * 60 * 14; // ~15 mins
    const errorWithinThreshold =
      this.credentialsTimestamp && this.credentialsTimestamp > currentTimestamp - errorThreshold;

    if (errorWithinThreshold || (error !== 'Tokeniser ERROR!' && error !== 'Proxy Authentication Required')) {
      this.snackBar.openFromComponent(GenericApiErrorComponent, { duration: 20000 });
    } else {
      this.setFieldValues(fieldValues);
      this.refreshCredentials$.next();
    }
  }

  private updateCardTypeInternal(cardType: IPaymentCardType) {
    this.url.searchParams.set('bin-ranges', this.getBinRangesParam(cardType?.bin_ranges));
    this.url.searchParams.set('card-number-length', cardType?.card_number_length?.toString());
    this.url.searchParams.set('ccv-max-length', cardType?.cvv_length?.toString());
  }

  private setCredentials(credentials: TokeniserCredentials) {
    if (credentials) {
      this.credentialsTimestamp = new Date().valueOf();

      this.url.searchParams.set('timestamp', credentials.timestamp.toString());
      this.url.searchParams.set('apiKey', credentials.apikey);
      this.url.searchParams.set('signature', credentials.signature);
    }
  }

  private setFieldValues(fieldValues?: IframeFormValues) {
    const params = {
      'card-number-token-value': fieldValues?.cardNumberToken,
      'card-holder-token-value': fieldValues?.cardHolderNameToken,
      'security-code-token-value': fieldValues?.securityCodeToken,
      'expiry-month': fieldValues?.expiryMonth,
      'expiry-year': fieldValues?.expiryYear,
      'validity-check': fieldValues?.validityCheck,
    };

    Object.entries(params).forEach(([key, value]) => {
      if (value) {
        this.url.searchParams.set(key, value);
      }
    });
  }

  private getBinRangesParam(binRanges?: BinRange[]): string {
    let binRangesParam = '';

    if (binRanges?.length) {
      binRangesParam = binRanges
        .map((param) => `${encodeURIComponent(param.lower)},${encodeURIComponent(param.upper)}`)
        .join(';');
    }

    return binRangesParam;
  }
}

@Injectable({ providedIn: 'root' })
export class TokeniserService {
  constructor(
    private http: HttpClient,
    private snackBar: MatSnackBar,
    private languageService: LanguageService,
    private siteSettingsService: SiteSettingsService
  ) {}

  public getTokeniserURL(
    site: TokeniserSite,
    cardType?: IPaymentCardType,
    fieldValues?: IframeFormValues,
    cssClass?: string
  ): TokeniserURL {
    const credentials$ = this.http.get<TokeniserCredentials>(ApiEndpoint.TOKENISER_CREDENTIALS);
    const language = this.languageService.currentLanguage;
    const direction = this.languageService.currentDirection;

    const clientName = this.siteSettingsService.settings.fe_configuration.path;
    return new TokeniserURL(
      this.snackBar,
      credentials$,
      site,
      cardType,
      fieldValues,
      language,
      direction,
      cssClass,
      clientName
    );
  }
}
